import { z } from "zod";
import { RecurrenceType, ScheduleWeekDay } from "@/types/staff-schedule";
import i18n from "@/i18n";

const t = (key: string) => i18n.t(`staffSchedule.${key}`);

export const createScheduleFormSchema = () =>
  z
    .object({
      staffId: z.string().min(1, { message: t("validation.staffRequired") }),
      recurrenceType: z.enum(RecurrenceType, {
        error: t("validation.recurrenceTypeRequired"),
      }),
      recurrenceInterval: z.number().optional(),
      days: z.array(z.enum(ScheduleWeekDay)).optional(),
      anchorDate: z
        .string()
        .min(1, { message: t("validation.anchorDateRequired") }),
      startTime: z
        .string()
        .min(1, { message: t("validation.startTimeRequired") }),
      endTime: z.string().min(1, { message: t("validation.endTimeRequired") }),
      effectiveFrom: z
        .string()
        .min(1, { message: t("validation.effectiveFromRequired") }),
      effectiveTo: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (
        data.recurrenceType === RecurrenceType.EVERY_N_DAYS &&
        (!data.recurrenceInterval || data.recurrenceInterval < 1)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.recurrenceIntervalRequired"),
          path: ["recurrenceInterval"],
        });
      }

      if (
        (data.recurrenceType === RecurrenceType.WEEKLY ||
          data.recurrenceType === RecurrenceType.BIWEEKLY) &&
        (!data.days || data.days.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validation.daysRequired"),
          path: ["days"],
        });
      }

      if (data.startTime && data.endTime) {
        const [sh, sm] = data.startTime.split(":").map(Number);
        const [eh, em] = data.endTime.split(":").map(Number);
        if (sh * 60 + sm >= eh * 60 + em) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("validation.endTimeAfterStart"),
            path: ["endTime"],
          });
        }
      }

      if (data.effectiveFrom && data.effectiveTo) {
        if (new Date(data.effectiveTo) <= new Date(data.effectiveFrom)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("validation.effectiveToAfterFrom"),
            path: ["effectiveTo"],
          });
        }
      }
    });

export type ScheduleFormSchema = ReturnType<typeof createScheduleFormSchema>;
export type ScheduleFormData = z.input<ScheduleFormSchema>;
